import React from "react";
import getAxios from "../../hooks/useAxios";
import Cookies from "js-cookie";
import {
  Avatar,
  Badge,
  Box,
  Flex,
  SimpleGrid,
  Text,
  useTheme,
  Image,
  useDisclosure,
  Icon,
  Skeleton,
} from "@chakra-ui/react";
import { DriverDetails } from "../Drivers/details";
import { Theme } from "../../theme";
import { useNavigate } from "react-router-dom";
import StatusConfirmationModal from "../../components/StatusConfirmModal";
import { MdArrowBackIosNew, MdArrowForwardIos } from "react-icons/md";

const ViewByDriver = () => {
  const axios = getAxios();
  const theme = useTheme<Theme>();
  const navigate = useNavigate();
  const access_token = Cookies.get("x-admin-auth");
  const params = new URLSearchParams(window.location.search);
  const id = params.get("id");

  const {
    isOpen: isPayIncentiveOpen,
    onOpen: onPayIncentiveOpen,
    onClose: onPayIncentiveClose,
  } = useDisclosure();

  const [ratings, setRatings] = React.useState<any>();
  const [driver, setDriver] = React.useState<DriverDetails>();
  const [loaded, setLoaded] = React.useState(true);
  const [dpImage, setDpFImage] = React.useState<string>();

  const [summary, setSummary] = React.useState<any>();
  const [incentives, setIncentives] = React.useState<any>([]);

  const [incentiveId, setIncentiveId] = React.useState("");

  const [pageNumber, setPageNumber] = React.useState(() => {
    return Number(localStorage.getItem("incentivebydriverpage")) || 1;
  });

  const [nextNotAllowed, setNextNotAllowed] = React.useState(false);

  const getIncentivesByDriverId = async (page: number = 1) => {
    try {
      setLoaded(true);

      const res = await axios.get(`/incentives?driver_id=${id}&page=${page}`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      setSummary(res.data?.summary);
      setIncentives(res.data?.data[0]?.incentives || []);

      // Adjust this based on your API response
      setNextNotAllowed(res.data?.data[0]?.incentives?.length === 0);
    } catch (error) {
      console.log("err", error);
    } finally {
      setLoaded(false);
    }
  };

  const markIncentivePaid = async () => {
    try {
      const res = await axios.patch(`/incentives/${incentiveId}/paid`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });
      console.log("paid ", res.data);
      navigate(0);
    } catch (error) {
      console.log("err", error);
    }
  };

  const markIncentivePaidAll = async () => {
    try {
      const res = await axios.patch(`/incentives/driver/${id}/pay-all`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });
      console.log("paid ", res.data);
      navigate(0);
      return alert("Incentive marked as paid!");
    } catch (error) {
      console.log("err", error);
    }
  };

  React.useEffect(() => {
    if (id) {
      getIncentivesByDriverId(pageNumber);
    }
  }, [id, pageNumber]);

  const formatIncentiveType = (type: string) => {
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  function arrayBufferToBase64(buffer: any) {
    let binary = "";
    let bytes = new Uint8Array(buffer);
    let len = bytes.length;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  const getImageLink = async (url: string, name: string) => {
    try {
      const res = await axios.get(`uploads/${url}?token=${access_token}`, {
        responseType: "arraybuffer",
      });
      const buffer = res.data;
      let base64String = arrayBufferToBase64(buffer);
      let dataUri = "data:image/jpeg;base64," + base64String;
      return dataUri;
    } catch (error) {
      console.log("error", error);
    }
  };

  const getDriverDetails = async (id: string) => {
    const res = await axios.get<DriverDetails>(`/drivers/${id}`);
    setDriver(res.data);
    if (res.data.documents) {
      await getImageLink(res.data.documents.profile_picture, "dp").then(
        (img) => {
          setDpFImage(img);
        },
      );
    }
    try {
    } catch (error) {}
  };

  const getUserRatings = async (id: string) => {
    try {
      const res = await axios.get(`/ratings/drivers/${id}`);

      const totalRating = Number(res.data?.total_rating) || 0;
      const ratingCount = Number(res.data?.rating_count) || 0;
      const ratings = ratingCount > 0 ? totalRating / ratingCount : 0;
      setRatings(ratings.toFixed(1));
    } catch (error) {
      console.log("error fetching ratings", error);
    }
  };

  React.useEffect(() => {
    if (id) {
      getDriverDetails(id);
      getUserRatings(id);
    }
  }, [id]);

  return (
    <Box sx={{ padding: "1rem" }}>
      <Box
        borderRadius="10px"
        border="1px solid"
        borderColor="gray.200"
        style={{
          width: "100%",
          margin: "1rem auto",
          boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
          borderRadius: "10px",
          background: "white",
        }}
      >
        <Flex
          sx={{
            borderBottom: "1px solid",
            borderColor: theme.colors.divider,
            padding: "0.75rem 1.25rem",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text sx={{ fontSize: "1.25rem", fontWeight: 900 }}>
            Driver Details
          </Text>
        </Flex>
        <Flex
          sx={{
            padding: "1.25rem 0 1rem 1.25rem",
          }}
        >
          <Flex sx={{ flex: 1, gap: "1rem", alignItems: "center" }}>
            <Box sx={{ position: "relative", width: "max-content" }}>
              <Avatar
                size={"xl"}
                name={driver?.name}
                src={dpImage}
                style={{
                  border: "2px solid #800020",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  padding: "0 0.125rem 0 0.5rem",
                  borderRadius: "0.5rem",
                  border: `2px solid ${theme.colors.primary.main}`,
                  background: "#FFFFFF",
                  bottom: "-10%",
                  left: "50%",
                  transform: "translate(-50%,0)",
                  width: "max-content",
                }}
              >
                <Flex sx={{ alignItems: "center" }}>
                  <Text
                    sx={{
                      color: theme.colors.primary.main,
                      fontSize: "0.85rem",
                      fontWeight: 900,
                    }}
                  >
                    {ratings ?? "--"}
                  </Text>
                  <Image
                    src={"/StarFilled.png"}
                    alt="No Images"
                    aspectRatio={1 / 1}
                    width="1.5rem"
                  />
                </Flex>
              </Box>
            </Box>
            <Box
              sx={{
                padding: "0.25rem 0.5rem",
                borderRadius: "0.5rem",
                border: `2px solid ${theme.colors.primary.main}`,
              }}
            >
              <Text
                sx={{
                  fontWeight: 600,
                  color: driver?.on_duty
                    ? theme.colors.success
                    : theme.colors.secondary.light,
                }}
              >
                {driver?.on_duty ? "ON DUTY" : "OFF DUTY"}
              </Text>
            </Box>
          </Flex>
          <Flex sx={{ flex: 1, justifyContent: "flex-end" }}>
            <Box
              sx={{
                marginRight: "1rem",
                padding: "0.5rem 1rem",
                cursor: "pointer",
                width: "max-content",
                height: "max-content",
                border: "1px solid",
                borderColor: theme.colors.primary.main,
                borderRadius: "0.5rem",
              }}
              onClick={() => {
                markIncentivePaidAll();
              }}
            >
              <Text
                sx={{
                  fontSize: "1rem",
                  fontWeight: 900,
                  color: theme.colors.primary.main,
                }}
              >
                Mark All Incentive as Paid
              </Text>
            </Box>
          </Flex>
        </Flex>
        <Flex
          sx={{
            padding: "1.25rem",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Flex sx={{ gap: "0.5rem" }}>
              <Text sx={{ color: theme.colors.secondary.light }}>Name:</Text>
              <Text sx={{ fontWeight: 600 }}>{driver?.name}</Text>
            </Flex>
            <Flex sx={{ gap: "0.5rem" }}>
              <Text sx={{ color: theme.colors.secondary.light }}>
                Phone Number:
              </Text>
              <Text sx={{ fontWeight: 600 }}>{driver?.mobile}</Text>
            </Flex>

            <Flex sx={{ gap: "0.5rem" }}>
              <Text sx={{ color: theme.colors.secondary.light }}>Vehicle:</Text>
              <Text sx={{ fontWeight: 600 }}>
                {driver?.vehicle_details
                  ? driver?.vehicle_details?.make +
                    " " +
                    driver?.vehicle_details?.model
                  : " --"}
              </Text>
            </Flex>

            <Flex sx={{ gap: "0.5rem" }}>
              <Text sx={{ color: theme.colors.secondary.light }}>
                Vehicle Registration Id:
              </Text>
              <Text sx={{ fontWeight: 600 }}>
                {driver?.vehicle_details?.registration_number || "   --"}
              </Text>
            </Flex>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Flex sx={{ gap: "0.5rem" }}>
              <Text sx={{ color: theme.colors.secondary.light }}>
                Joining Date:
              </Text>
              <Text sx={{ fontWeight: 600 }}>
                {(driver &&
                  new Date(driver.created_at).toLocaleDateString("en-IN")) ||
                  "   --"}
              </Text>
            </Flex>
            <Flex sx={{ gap: "0.5rem" }}>
              <Text sx={{ color: theme.colors.secondary.light }}>
                Account Status:
              </Text>
              <Text
                sx={{
                  fontWeight: 600,
                  color: driver?.is_blocked
                    ? theme.colors.error
                    : theme.colors.success,
                }}
              >
                {driver?.is_blocked ? "Blocked" : "Active"}
              </Text>
            </Flex>
            <Flex sx={{ gap: "0.5rem" }}>
              <Text sx={{ color: theme.colors.secondary.light }}>
                On Boarding Status:
              </Text>
              <Text
                sx={{
                  fontWeight: 600,
                  color: driver?.is_finished_onboarding
                    ? theme.colors.success
                    : theme.colors.warning,
                }}
              >
                {driver?.is_finished_onboarding ? "Completed" : "Pending"}
              </Text>
            </Flex>

            <Flex sx={{ gap: "0.5rem" }}>
              <Text sx={{ color: theme.colors.secondary.light }}>
                Documentation Approval:
              </Text>
              <Text
                sx={{
                  fontWeight: 600,
                  color: driver?.is_approved
                    ? theme.colors.success
                    : theme.colors.warning,
                }}
              >
                {driver?.is_approved ? "Approved" : "Not Approved"}
              </Text>
            </Flex>

            {/* <Flex sx={{ gap: "0.5rem" }}>
                    <Text sx={{ color: theme.colors.secondary.light }}>
                      Duty Status:
                    </Text>
                    <Text sx={{ fontWeight: 600 }}>
                      {driver?.on_duty ? "On Duty" : "Off Duty"}
                    </Text>
                  </Flex> */}
          </Box>
        </Flex>
      </Box>

      <Box
        bg="white"
        borderRadius="10px"
        border="1px solid"
        borderColor="gray.200"
        overflow="hidden"
      >
        <Box px={5} py={4} borderBottom="1px solid" borderColor="gray.200">
          <Text fontSize="18px" fontWeight="700">
            Incentive Summary
          </Text>
        </Box>

        <Box p={5}>
          {/* Main summary cards */}
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
            <Box
              p={4}
              borderRadius="8px"
              bg="gray.50"
              border="1px solid"
              borderColor="gray.100"
            >
              <Text fontSize="13px" color="gray.500">
                Total Incentives
              </Text>

              <Text mt={1} fontSize="24px" fontWeight="700">
                ₹{summary?.total_amount || 0}
              </Text>

              <Text fontSize="12px" color="gray.500">
                {summary?.total_count || 0} incentives
              </Text>
            </Box>

            <Box
              p={4}
              borderRadius="8px"
              bg="red.50"
              border="1px solid"
              borderColor="red.100"
            >
              <Text fontSize="13px" color="gray.500">
                Unpaid Amount
              </Text>

              <Text mt={1} fontSize="24px" fontWeight="700" color="red.600">
                ₹{summary?.unpaid_amount || 0}
              </Text>

              <Text fontSize="12px" color="gray.500">
                {summary?.unpaid_count || 0} unpaid
              </Text>
            </Box>

            <Box
              p={4}
              borderRadius="8px"
              bg="green.50"
              border="1px solid"
              borderColor="green.100"
            >
              <Text fontSize="13px" color="gray.500">
                Paid Amount
              </Text>

              <Text mt={1} fontSize="24px" fontWeight="700" color="green.600">
                ₹{(summary?.total_amount || 0) - (summary?.unpaid_amount || 0)}
              </Text>

              <Text fontSize="12px" color="gray.500">
                {summary?.total_count - summary?.unpaid_count || 0} paid
              </Text>
            </Box>

            <Box
              p={4}
              borderRadius="8px"
              bg="purple.50"
              border="1px solid"
              borderColor="purple.100"
            >
              <Text fontSize="13px" color="gray.500">
                Incentive Types
              </Text>

              <Text mt={1} fontSize="24px" fontWeight="700" color="purple.600">
                {Object.keys(summary?.by_type || {}).length}
              </Text>

              <Text fontSize="12px" color="gray.500">
                Active incentive categories
              </Text>
            </Box>
          </SimpleGrid>

          {/* Breakdown */}
          <Box mt={6}>
            <Text mb={3} fontSize="15px" fontWeight="600">
              Breakdown by Type
            </Text>

            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="8px"
              overflow="hidden"
            >
              <Flex
                px={4}
                py={3}
                bg="gray.50"
                borderBottom="1px solid"
                borderColor="gray.200"
                fontSize="13px"
                fontWeight="600"
                color="gray.600"
              >
                <Box flex={2}>Incentive Type</Box>
                <Box flex={1}>Count</Box>
                <Box flex={1}>Amount</Box>
                <Box flex={1}>Unpaid</Box>
              </Flex>

              {Object.entries(summary?.by_type || {}).map(
                ([type, item]: [string, any]) => (
                  <Flex
                    key={type}
                    px={4}
                    py={3}
                    alignItems="center"
                    borderBottom="1px solid"
                    borderColor="gray.100"
                    fontSize="14px"
                    _last={{ borderBottom: "none" }}
                  >
                    <Box flex={2}>
                      <Text fontWeight="500">{formatIncentiveType(type)}</Text>
                    </Box>

                    <Box flex={1}>
                      <Text>{item.count}</Text>
                    </Box>

                    <Box flex={1}>
                      <Text fontWeight="600">₹{item.amount}</Text>
                    </Box>

                    <Box flex={1}>
                      <Text color="red.600" fontWeight="500">
                        ₹{item.unpaid_amount}
                      </Text>
                    </Box>
                  </Flex>
                ),
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        bg="white"
        borderRadius="10px"
        border="1px solid"
        borderColor="gray.200"
        overflow="hidden"
        marginTop={"1rem"}
      >
        <Box px={5} py={4} borderBottom="1px solid" borderColor="gray.200">
          <Flex justifyContent="space-between" alignItems="center">
            <Box>
              <Text fontSize="18px" fontWeight="700">
                Incentives
              </Text>

              <Text mt={1} fontSize="13px" color="gray.500">
                Incentive earnings generated from completed rides
              </Text>
            </Box>

            <Box
              px={3}
              py={1}
              borderRadius="20px"
              bg="gray.100"
              fontSize="13px"
              fontWeight="600"
            >
              {incentives?.length || 0} Records
            </Box>
          </Flex>
        </Box>

        <Box p={4}>
          {/* Header */}
          <Flex
            px={4}
            py={3}
            bg="gray.50"
            borderRadius="7px"
            fontSize="12px"
            fontWeight="600"
            color="gray.500"
            textTransform="uppercase"
          >
            <Box flex={2}>Incentive</Box>
            <Box flex={1}>Amount</Box>
            {/* <Box flex={1.3}>Driver ID</Box>
            <Box flex={1.3}>Ride ID</Box> */}
            <Box flex={1.2}>Date</Box>
            <Box flex={0.8}>Status</Box>
            <Box flex={0.8}>Action</Box>
          </Flex>

          {/* Data */}
          <Box mt={1}>
            {incentives?.map((item: any) => (
              <Flex
                key={item.id}
                px={4}
                py={4}
                alignItems="center"
                borderBottom="1px solid"
                borderColor="gray.100"
                fontSize="14px"
                _last={{ borderBottom: "none" }}
                _hover={{
                  bg: "gray.50",
                }}
                transition="0.2s"
              >
                {/* Incentive Type */}
                <Box flex={2}>
                  <Text fontWeight="600">{formatIncentiveType(item.type)}</Text>

                  <Text mt={1} fontSize="12px" color="gray.400">
                    Driver incentive
                  </Text>
                </Box>

                {/* Amount */}
                <Box flex={1}>
                  <Text fontSize="15px" fontWeight="700">
                    ₹{item.amount}
                  </Text>
                </Box>

                {/* Driver ID */}
                {/* <Box flex={1.3}>
                  <Text
                    fontSize="12px"
                    color="gray.500"
                    noOfLines={1}
                    title={item.driver_id}
                  >
                    {item.driver_id}
                  </Text>
                </Box> */}

                {/* Ride ID */}
                {/* <Box flex={1.3}>
                  <Text
                    fontSize="12px"
                    color="gray.500"
                    noOfLines={1}
                    title={item.ride_id}
                  >
                    {item.ride_id}
                  </Text>
                </Box> */}

                {/* Date */}
                <Box flex={1.2}>
                  <Text fontSize="13px">
                    {new Date(item.created_at).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </Text>

                  <Text mt={1} fontSize="11px" color="gray.400">
                    {new Date(item.created_at).toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Text>
                </Box>

                {/* Status */}
                <Box flex={0.8}>
                  <Badge
                    px={3}
                    py={1}
                    borderRadius="20px"
                    colorScheme={item.is_paid ? "green" : "orange"}
                    fontSize="11px"
                  >
                    {item.is_paid ? "Paid" : "Unpaid"}
                  </Badge>
                </Box>

                {/* Payment */}
                <Box flex={0.8}>
                  <Badge
                    px={3}
                    py={1}
                    borderRadius="20px"
                    colorScheme={"purple"}
                    fontSize="11px"
                    cursor={item.is_paid ? "not-allowed" : "pointer"}
                    onClick={() => {
                      if (item.is_paid) return;
                      onPayIncentiveOpen();
                      setIncentiveId(item.id);
                    }}
                  >
                    Pay Now
                  </Badge>
                </Box>
              </Flex>
            ))}
            <StatusConfirmationModal
              heading="Are you sure incentive is paid ?"
              isOpen={isPayIncentiveOpen}
              onClose={onPayIncentiveClose}
              handleApiCall={() => markIncentivePaid()}
            />
          </Box>

          {/* <Box
            sx={{
              marginTop: theme.space.xl,
            }}
          >
            <Flex
              sx={{
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "3rem",
              }}
            >
              <Box>
                <Flex
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Flex
                    sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%",
                      width: "2rem",
                      height: "2rem",
                    }}
                  >
                    <Icon
                      as={MdArrowBackIosNew}
                      boxSize={5}
                      color={theme.colors.primary.main}
                      cursor={pageNumber !== 1 ? "pointer" : "not-allowed"}
                      onClick={() => {
                        if (pageNumber !== 1) {
                          setPageNumber(pageNumber - 1);
                          localStorage.setItem(
                            "incentivebydriverpage",
                            `${pageNumber - 1}`,
                          );
                        }
                      }}
                    />
                  </Flex>
                  <Text variant={"caption"}>Previous Page</Text>
                </Flex>
              </Box>
              <Skeleton isLoaded={loaded ? false : true} sx={{ width: "2rem" }}>
                <Text variant={"body"}>{pageNumber}</Text>
              </Skeleton>
              <Box>
                <Flex
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Text variant={"caption"}>Next Page</Text>
                  <Flex
                    sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%",
                      width: "2rem",
                      height: "2rem",
                    }}
                  >
                    <Icon
                      as={MdArrowForwardIos}
                      boxSize={5}
                      color={theme.colors.primary.main}
                      cursor={nextNotAllowed ? "not-allowed" : "pointer"}
                      onClick={() => {
                        if (!nextNotAllowed) {
                          setPageNumber(pageNumber + 1);
                          localStorage.setItem(
                            "incentivebydriverpage",
                            `${pageNumber + 1}`,
                          );
                        }
                      }}
                    />
                  </Flex>
                </Flex>
              </Box>
            </Flex>
          </Box> */}
        </Box>
      </Box>
    </Box>
  );
};

export default ViewByDriver;
